import React, { useEffect } from 'react';

const Quote = ({ children }) => (
  <div style={{ background: 'rgba(30, 58, 95, 0.08)', borderLeft: '4px solid #1e3a5f', padding: '1.25rem 1.5rem', borderRadius: '8px', margin: '1rem 0', fontStyle: 'italic', lineHeight: 1.8, color: '#2d2d2d' }}>
    {children}
  </div>
);

const H2 = ({ children }) => <h2 style={{ color: '#1e3a5f', marginTop: '2.5rem', marginBottom: '1rem' }}>{children}</h2>;
const H3 = ({ children }) => <h3 style={{ color: '#2d4a6f', marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '1.2rem' }}>{children}</h3>;

const RelatedLinks = ({ current }) => {
  const all = Object.entries(PAGES).filter(([k]) => k !== current);
  const show = all.slice(0, 5);
  return (
    <div style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid rgba(30,58,95,0.15)' }}>
      <H2>Related Guides</H2>
      <ul style={{ listStyle: 'none', padding: 0 }}>
        {show.map(([path, page]) => (
          <li key={path} style={{ marginBottom: '0.5rem' }}>
            <a href={path} style={{ color: '#1e3a5f', textDecoration: 'underline' }}>{page.title}</a>
          </li>
        ))}
      </ul>
    </div>
  );
};

const CTA = ({ onGetStarted }) => (
  <div style={{ textAlign: 'center', margin: '2.5rem 0 1rem' }}>
    <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>Need help writing your vows? Our AI-powered tool creates beautiful, personalized wedding vows in minutes.</p>
    <button className="btn btn-lg" onClick={onGetStarted} style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #1a365d 100%)', color: 'white', border: 'none', padding: '0.75rem 2rem', borderRadius: '50px', fontWeight: 600 }}>
      Write My Vows Now
    </button>
  </div>
);

// ─── PAGE CONTENT ───────────────────────────────────────────────

const PAGES = {

  '/wedding-vow-examples-for-him': {
    title: 'Wedding Vow Examples for Him — What to Say to Your Groom',
    description: 'Beautiful wedding vow examples for him. Romantic, funny, simple, and emotional vows a bride can say to her groom.',
    render: (onGetStarted) => (<>
      <h1 className="commercial-heading mb-3">Wedding Vow Examples for Him</h1>
      <p>Finding the right words to say to the man you love on your wedding day can feel overwhelming. These examples range from deeply romantic to sweetly funny — use them as inspiration or customize them to tell your unique story.</p>

      <CTA onGetStarted={onGetStarted} />

      <H2>Romantic & Emotional</H2>
      <Quote>
        <p>I spent my whole life looking for something I couldn't name — and then I met you, and suddenly everything made sense. You are my calm in every storm, my laughter at the end of hard days, and the first person I want to tell anything to. I choose you today, tomorrow, and every day after. Not because I have to, but because choosing you is the easiest and best decision I've ever made.</p>
      </Quote>

      <Quote>
        <p>Before you, I thought love was something you had to earn. You taught me that real love is given freely, without conditions, without scorekeeping. You see me — all of me — and you love me anyway. Today I promise to spend our lives making sure you feel as seen and loved as you've made me feel.</p>
      </Quote>

      <H2>Sweet & Simple</H2>
      <Quote>
        <p>I love who I am when I'm with you. I love how you make coffee without being asked, how you hold my hand in the car, and how you always let me have the last bite. I promise to be your partner, your best friend, and your biggest fan for the rest of our lives.</p>
      </Quote>

      <Quote>
        <p>You are my favorite person. My favorite hello and my hardest goodbye. Today I promise to laugh with you, cry with you, grow with you, and love you — in sickness, in health, in good times, in Netflix binges, and in everything in between.</p>
      </Quote>

      <H2>Funny & Lighthearted</H2>
      <Quote>
        <p>I promise to love you even when you leave your socks on the floor, even when you insist you're right (you're usually not), and even when you steal all the covers. I promise to always laugh at your jokes — the good ones enthusiastically, the bad ones politely. And I promise that when we're old and gray, I'll still look at you the way I'm looking at you right now. Except I'll probably need glasses.</p>
      </Quote>

      <Quote>
        <p>My mom told me I'd know when I found "the one." I was skeptical. Then I met a man who brought me tacos at midnight when I was studying, who memorized my coffee order on our second date, and who once drove two hours in the rain just because I said I missed him. Mom was right. You're the one. And I promise to spend our marriage proving that you made the right choice too.</p>
      </Quote>

      <H2>Poetic & Literary</H2>
      <Quote>
        <p>You are the poem I never knew I was writing — every line of my life was leading to you. In your eyes I see my future. In your hands I place my heart. In your arms I find my home. Today, in front of everyone we love, I give you my yesterday, my today, and every tomorrow that follows.</p>
      </Quote>

      <H2>Tips for Personalizing Vows for Him</H2>
      <ul>
        <li><strong>Reference your story:</strong> How you met, your first date, or the moment you knew</li>
        <li><strong>Be specific:</strong> "I love how you sing in the shower" is more powerful than "I love everything about you"</li>
        <li><strong>Include promises:</strong> At least 2-3 concrete commitments beyond "I love you"</li>
        <li><strong>Match his personality:</strong> If he's funny, humor belongs. If he's sentimental, go deep.</li>
        <li><strong>Keep it to 1-2 minutes:</strong> 150-300 words is the sweet spot</li>
      </ul>

      <CTA onGetStarted={onGetStarted} />
    </>),
  },

  '/wedding-vow-examples-for-her': {
    title: 'Wedding Vow Examples for Her — What to Say to Your Bride',
    description: 'Heartfelt wedding vow examples for her. Sweet, romantic, humorous, and traditional vows a groom can say to his bride.',
    render: (onGetStarted) => (<>
      <h1 className="commercial-heading mb-3">Wedding Vow Examples for Her</h1>
      <p>Writing vows for the woman you love means finding words that match the depth of what you feel. Whether you're naturally eloquent or a man of few words, these examples will help you express what's in your heart.</p>

      <CTA onGetStarted={onGetStarted} />

      <H2>Deeply Romantic</H2>
      <Quote>
        <p>The day I met you, my life split into two parts — before you and after you. Everything after is better. You make ordinary days extraordinary. You make me braver, kinder, and more myself than I ever was alone. I promise to spend every day earning the way you look at me right now.</p>
      </Quote>

      <Quote>
        <p>I've never been good with words. I'm better with actions. So when I say I love you, know that behind those three words is every late-night conversation, every airport pickup, every time I chose your happiness over my own comfort. Today I promise you all my actions, all my days, all my love — for the rest of my life.</p>
      </Quote>

      <H2>Sweet & Sincere</H2>
      <Quote>
        <p>You are the most beautiful person I've ever known — and I don't just mean the way you look, although that doesn't hurt. I mean the way you care for people, the way you see beauty in small things, the way you make everyone around you feel like they matter. I promise to protect that light in you. I promise to never take it for granted. And I promise to try, every single day, to be worthy of your love.</p>
      </Quote>

      <Quote>
        <p>I loved you before I knew what to call it. You were my best friend first, and somehow that turned into the greatest love story I could imagine. Today I promise to keep being your best friend — to listen when you need to talk, to hold you when you need comfort, and to always save you the window seat.</p>
      </Quote>

      <H2>With Humor</H2>
      <Quote>
        <p>I promise to kill the spiders. I promise to always let you control the thermostat, even though 74 degrees is objectively too warm. I promise to pretend I don't see you stealing my French fries and to always tell you that dress looks amazing. But mostly, I promise to love you with everything I have — even on the days when you make me crazy. Especially on those days.</p>
      </Quote>

      <Quote>
        <p>When I told my friends I was writing my own vows, they laughed. "You?" they said. "The guy who texts in all lowercase?" But here's the thing — you make me want to try harder. To capitalize. To use full sentences. To find the right words. Because you deserve the right words. So here they are: I love you. I choose you. And I will keep choosing you, every single day, for the rest of our lives.</p>
      </Quote>

      <H2>Traditional with a Modern Touch</H2>
      <Quote>
        <p>I take you as my wife, my partner, and my best friend. I promise to love you in plenty and in want, in sickness and in health, in failure and in triumph. I promise to dream with you, to support your dreams, and to build a life that makes us both proud. Where you go, I will go. Your people will be my people. And my heart will be yours — today, tomorrow, and always.</p>
      </Quote>

      <H2>What Women Love to Hear in Vows</H2>
      <ul>
        <li><strong>Vulnerability:</strong> Admitting she makes you nervous, that you cried thinking about this moment — authenticity is attractive</li>
        <li><strong>Specifics about her:</strong> What exactly you love about her, not just generic "you're beautiful"</li>
        <li><strong>Your story:</strong> The moment you knew, the moment you fell, the moment you decided to propose</li>
        <li><strong>Real promises:</strong> Not just "I'll love you forever" but "I'll bring you coffee every morning"</li>
        <li><strong>Acknowledgment of her strength:</strong> Women want to be seen as whole people, not just loved objects</li>
      </ul>

      <CTA onGetStarted={onGetStarted} />
    </>),
  },

  '/wedding-vow-examples-for-them': {
    title: 'Gender-Neutral Wedding Vow Examples — Inclusive Vows for Any Couple',
    description: 'Beautiful gender-neutral wedding vow examples. Modern, inclusive vows suitable for any couple, any ceremony.',
    render: (onGetStarted) => (<>
      <h1 className="commercial-heading mb-3">Gender-Neutral Wedding Vow Examples</h1>
      <p>Love doesn't fit into boxes, and neither should your vows. These examples use inclusive, gender-neutral language that works beautifully for any couple — regardless of gender identity or expression.</p>

      <CTA onGetStarted={onGetStarted} />

      <H2>Heartfelt & Universal</H2>
      <Quote>
        <p>I have found in you a partner, a home, and a future I never dared to dream of. You see me — really see me — and you love what you see. That is the rarest gift anyone has ever given me. Today I promise to see you just as clearly, to love you just as deeply, and to walk beside you wherever life takes us.</p>
      </Quote>

      <Quote>
        <p>Before you, I didn't know that love could feel this safe. With you, I can be exactly who I am — no masks, no performance, no pretending. You make my truest self feel like enough. Today, in front of the people who matter most, I promise to give you that same safety, that same acceptance, for the rest of our lives.</p>
      </Quote>

      <H2>Modern & Bold</H2>
      <Quote>
        <p>I don't believe in fairytales. I believe in something better — two real people choosing each other, imperfections and all, every single day. You are my real-life love story, and it's better than any fiction. I choose you today. I'll choose you tomorrow. And I'll keep choosing you until choosing isn't something I do anymore — it's just who I am.</p>
      </Quote>

      <Quote>
        <p>They say love is patient, love is kind. But they don't mention that love is also someone who knows your coffee order, who holds your hand during turbulence, who stays up late talking about nothing, and who makes the world feel less overwhelming just by being in it. That's what you are to me. And today I promise to be that for you.</p>
      </Quote>

      <H2>Short & Powerful</H2>
      <Quote>
        <p>You are my person. My partner. My favorite adventure. I promise to love you fiercely, support you completely, and laugh with you always. Today and every day.</p>
      </Quote>

      <Quote>
        <p>I promise to be honest with you, to be kind to you, to grow with you, and to build a life with you that we're both proud of. You are my home, and I am yours.</p>
      </Quote>

      <H2>Poetic & Philosophical</H2>
      <Quote>
        <p>In a universe of infinite possibilities, the odds of us finding each other were astronomically small. Yet here we are — two people who looked across a crowded world and recognized something in each other that felt like home. I don't take that for granted. Not for a second. Today I promise to honor the miracle of us — to nurture it, protect it, and never stop being amazed by it.</p>
      </Quote>

      <H2>Tips for Writing Inclusive Vows</H2>
      <ul>
        <li><strong>Focus on the relationship:</strong> What you've built together transcends gender</li>
        <li><strong>Use "partner," "person," "love"</strong> instead of gendered terms if preferred</li>
        <li><strong>Center your unique story:</strong> No two love stories are the same — lean into yours</li>
        <li><strong>Don't feel pressure to follow templates:</strong> Your vows should sound like you, not like anyone else</li>
        <li><strong>Coordinate length and tone:</strong> Chat with your partner about general approach so your vows complement each other</li>
      </ul>

      <CTA onGetStarted={onGetStarted} />
    </>),
  },

  '/short-funny-romantic-traditional-vows': {
    title: 'Wedding Vows by Style — Short, Funny, Romantic & Traditional Examples',
    description: 'Wedding vow examples organized by style: short vows, funny vows, romantic vows, and traditional vows. Find the perfect match.',
    render: (onGetStarted) => (<>
      <h1 className="commercial-heading mb-3">Wedding Vows by Style</h1>
      <p>Your vows should match your personality and your wedding. A beach elopement calls for something different than a cathedral ceremony. Here are examples organized by the four most popular styles.</p>

      <CTA onGetStarted={onGetStarted} />

      <H2>Short Vows (Under 100 Words)</H2>
      <p>Perfect for: elopements, intimate ceremonies, or if you know you'll be too emotional for long vows.</p>
      <Quote><p>I love you. I am proud to be with you. I promise to support your dreams, celebrate your victories, and hold you through everything else. You are my today and all of my tomorrows.</p></Quote>
      <Quote><p>You make me better. You make me braver. You make every day worth waking up for. I promise to spend our lives doing the same for you.</p></Quote>
      <Quote><p>I choose you. In every version of my life, in every universe, I would find you and I would choose you. Today I make that choice official.</p></Quote>

      <H2>Funny Vows</H2>
      <p>Perfect for: couples who make each other laugh, casual ceremonies, or breaking the nervous tension.</p>
      <Quote><p>I vow to always let you have the last slice of pizza — unless it's pepperoni, in which case all bets are off. I vow to laugh at your jokes, even the terrible ones, and to only judge you a little for your taste in reality TV. I vow to love you when you're hangry, when you're stressed, and when you insist on using seventeen throw pillows. You're my favorite weirdo, and I can't wait to be weird with you forever.</p></Quote>
      <Quote><p>I promise to love you in sickness and in health, in good times and in bad, in summer and in that weird week between Christmas and New Year's when neither of us knows what day it is. I promise to always charge your phone when it's dying and to never spoil the ending of a show you haven't watched yet. That's real love.</p></Quote>

      <H3>Tips for funny vows:</H3>
      <ul>
        <li>Open with humor, close with sincerity — the emotional pivot is what makes it powerful</li>
        <li>Inside jokes work great but include enough context for your guests</li>
        <li>Avoid jokes that punch down or embarrass your partner</li>
      </ul>

      <H2>Romantic Vows</H2>
      <p>Perfect for: traditional venues, couples who want the full emotional experience, or if your partner is a romantic.</p>
      <Quote><p>From the moment I met you, I knew my life would never be the same. You awakened something in me that I didn't know was sleeping — a capacity for love so deep it takes my breath away. Today, as I stand before you, I am overwhelmed by the certainty that I was made to love you. I promise to cherish you, to honor you, and to build a life with you that is worthy of a love this extraordinary.</p></Quote>
      <Quote><p>If I could go back to every moment before I met you and whisper one thing to myself, it would be: "Wait. It's coming. It's worth it." Because you are worth every moment of waiting, every wrong turn, every closed door that led me to you. You are the love of my life, and I will spend the rest of it making sure you know it.</p></Quote>

      <H2>Traditional Vows (Modern Language)</H2>
      <p>Perfect for: religious ceremonies, formal weddings, or couples who love the classics but want a personal touch.</p>
      <Quote><p>I take you to be my [husband/wife/partner], to have and to hold from this day forward. I promise to love you in plenty and in want, in joy and in sorrow, in sickness and in health. I will respect you, support you, and walk beside you all the days of our lives. This is my solemn promise, made with a full and grateful heart.</p></Quote>
      <Quote><p>Before God and these witnesses, I pledge you my love and my life. I will be faithful to you in good times and in bad, in prosperity and in hardship. I will love you and honor you all the days of my life. With this ring, I give you my heart, my devotion, and my unwavering commitment to our marriage.</p></Quote>

      <H2>How to Choose Your Style</H2>
      <ul>
        <li><strong>Match your wedding:</strong> A rustic barn wedding has a different vibe than a black-tie affair</li>
        <li><strong>Match your partner:</strong> Write for an audience of one — what will move them?</li>
        <li><strong>Match yourselves:</strong> If you never say "I pledge thee my troth" in real life, don't say it in your vows</li>
        <li><strong>Mix and match:</strong> A funny opening with a romantic close is one of the most powerful combinations</li>
      </ul>

      <CTA onGetStarted={onGetStarted} />
    </>),
  },

  '/vow-renewal-vows': {
    title: 'Vow Renewal Vows — Examples for Every Anniversary Milestone',
    description: 'Vow renewal examples for 10-year, 25-year, and 50-year anniversaries. Celebrate your journey with updated vows.',
    render: (onGetStarted) => (<>
      <h1 className="commercial-heading mb-3">Vow Renewal Vows</h1>
      <p>Renewing your vows isn't about repeating what you said on your wedding day — it's about reflecting on everything you've been through since. These examples capture the depth that only years of real love can provide.</p>

      <CTA onGetStarted={onGetStarted} />

      <H2>10-Year Renewal</H2>
      <Quote>
        <p>Ten years ago, I promised to love you. I had no idea what that really meant. I didn't know about the 3 AM feedings, the career changes, the arguments about whose turn it was to take out the trash. I didn't know that love wasn't just the butterflies — it was also the Tuesday nights on the couch, the grocery store runs, and the quiet moments after the kids finally fall asleep. Now I know. And knowing makes me love you more. I'd choose you again. I'd choose all of it again.</p>
      </Quote>

      <H2>25-Year (Silver Anniversary) Renewal</H2>
      <Quote>
        <p>Twenty-five years. A quarter century of mornings waking up next to you, and I still haven't gotten tired of your face. We've built a life I'm proud of — not a perfect one, but a real one. We've survived things that would have broken lesser teams. We raised humans who turned out pretty well. And through all of it, you remained the person I wanted to talk to at the end of every day. Today I renew my promise: I am yours, you are mine, and the best is still ahead of us.</p>
      </Quote>

      <H2>50-Year (Golden Anniversary) Renewal</H2>
      <Quote>
        <p>Fifty years ago, I was young and naive and hopelessly in love with you. Today, I am none of those things — except the last one. I am still hopelessly in love with you. You have been my partner through five decades of change. The world we live in looks nothing like the one we started in, but you — you are still you. Still kind, still strong, still the person who makes me laugh. I don't have the words for fifty years of gratitude. So I'll simply say: thank you, my love. For everything. For all of it.</p>
      </Quote>

      <H2>After Hardship</H2>
      <Quote>
        <p>We almost didn't make it. There were days when I wondered if we would. But here we are — not despite the hard times, but because of what we chose to do with them. We chose to fight for each other instead of with each other. We chose to rebuild instead of walk away. Today I renew my vows not as the person who married you — but as the person who stayed, who fought, and who would do it all again.</p>
      </Quote>

      <H2>Lighthearted Renewal</H2>
      <Quote>
        <p>When I married you, I promised for better or for worse. The "better" has been extraordinary. The "worse" has been... character-building. I've loved you through bad haircuts, questionable cooking experiments, and that time you bought a boat. And you've loved me through all of my nonsense too. That's the deal. That's our deal. And I wouldn't trade it for anything. Here's to whatever comes next.</p>
      </Quote>

      <H2>Tips for Vow Renewals</H2>
      <ul>
        <li><strong>Reflect on your journey:</strong> Unlike original vows, renewal vows can reference real experiences</li>
        <li><strong>Acknowledge growth:</strong> You're not the same people who got married — celebrate that</li>
        <li><strong>Be honest:</strong> Mentioning challenges you've overcome adds depth and authenticity</li>
        <li><strong>Keep it conversational:</strong> You're renewing, not performing — speak naturally</li>
        <li><strong>Include gratitude:</strong> Thank your partner for specific things they've done over the years</li>
      </ul>

      <CTA onGetStarted={onGetStarted} />
    </>),
  },

  '/non-religious-wedding-vows': {
    title: 'Non-Religious Wedding Vows — Secular & Meaningful Examples',
    description: 'Beautiful non-religious wedding vow examples that are deeply meaningful without references to God or scripture.',
    render: (onGetStarted) => (<>
      <h1 className="commercial-heading mb-3">Non-Religious Wedding Vows</h1>
      <p>You don't need scripture to make your vows sacred. Secular vows can be just as profound, drawing from philosophy, literature, personal experience, and the sheer weight of human love. Here are examples that are meaningful without being religious.</p>

      <CTA onGetStarted={onGetStarted} />

      <H2>Philosophical & Deep</H2>
      <Quote>
        <p>I don't believe love is something that happens to us. I believe it's something we build, brick by brick, choice by choice, day after day. Today I choose to build with you. I promise to show up, to do the work, and to never stop choosing us — even when it's hard. Especially when it's hard.</p>
      </Quote>

      <Quote>
        <p>In all the vastness of time and space, we found each other. I don't know if that's fate or luck or just beautiful coincidence, but I know this: I will not waste it. I promise to honor this improbable, extraordinary thing we've found by loving you with intention, with courage, and with my whole heart.</p>
      </Quote>

      <H2>Nature-Inspired</H2>
      <Quote>
        <p>Like the ocean shapes the shore, you have shaped me — slowly, beautifully, and irreversibly. I promise to be your steady ground when the waves are high. I promise to grow with you like roots intertwined. And I promise that my love for you will be like the tide — constant, powerful, and always coming back.</p>
      </Quote>

      <H2>Literature-Inspired</H2>
      <Quote>
        <p>Pablo Neruda wrote, "I love you without knowing how, or when, or from where." That's how it started for me — a love I couldn't explain, couldn't contain, couldn't resist. But today I promise to love you in all the ways I do know: with patience, with honesty, with kindness, and with the certainty that you are the great adventure of my life.</p>
      </Quote>

      <H2>Practical & Grounded</H2>
      <Quote>
        <p>I promise to be honest, even when it's uncomfortable. To listen, even when I think I know the answer. To apologize when I'm wrong and to forgive when you are. I promise to dream with you, plan with you, and figure it out with you — whatever "it" turns out to be. Life is uncertain. But my love for you is not.</p>
      </Quote>

      <Quote>
        <p>I don't promise you perfection — I promise you effort. I promise to try every day to be the partner you deserve. To communicate, to compromise, to celebrate, and to grow. I promise that when things get hard, I will reach for your hand instead of pulling away. This is my commitment to you, and I make it freely and completely.</p>
      </Quote>

      <H2>Sources of Inspiration Beyond Religion</H2>
      <ul>
        <li><strong>Poetry:</strong> Neruda, Rumi, Mary Oliver, E.E. Cummings, Rilke</li>
        <li><strong>Philosophy:</strong> Stoicism, existentialism, or your own personal philosophy</li>
        <li><strong>Science:</strong> The wonder of human connection, the improbability of existence</li>
        <li><strong>Literature:</strong> Favorite novels, movies, or songs that define your relationship</li>
        <li><strong>Your own experience:</strong> Your story together is the most authentic source of all</li>
      </ul>

      <CTA onGetStarted={onGetStarted} />
    </>),
  },

  '/same-sex-wedding-vows': {
    title: 'Same-Sex Wedding Vows — Beautiful Examples for Every Couple',
    description: 'Wedding vow examples for same-sex couples. Celebrate your unique love story with vows that honor who you are.',
    render: (onGetStarted) => (<>
      <h1 className="commercial-heading mb-3">Same-Sex Wedding Vows</h1>
      <p>Your love story is uniquely yours, and your vows should reflect that. These examples celebrate the strength, courage, and joy of love that transcends convention — written for couples who've always known that love is love.</p>

      <CTA onGetStarted={onGetStarted} />

      <H2>Celebrating Your Unique Journey</H2>
      <Quote>
        <p>Loving you was never the brave part. Loving you was the easiest thing I've ever done. The brave part was showing the world. Today, standing here in front of everyone who matters, I am filled with a pride I can barely contain. I am proud to be yours. I am proud of us. And I promise to love you openly, loudly, and without apology for the rest of our lives.</p>
      </Quote>

      <Quote>
        <p>We didn't get here the easy way. There were doors that closed, people who didn't understand, and moments when the world felt stacked against us. But you held my hand through all of it. Today, I promise to hold yours — through every challenge, every celebration, and every ordinary Tuesday that makes up this extraordinary life we're building together.</p>
      </Quote>

      <H2>Romantic & Timeless</H2>
      <Quote>
        <p>I loved you before I had the language for it. Before I had the courage for it. Before the world caught up to what my heart already knew. You are the love story I was always meant to live. Today I promise you my whole heart, my unwavering support, and a lifetime of being exactly who we are — together.</p>
      </Quote>

      <Quote>
        <p>You walked into my life and made everything clearer. The noise quieted. The doubts faded. You looked at me and I thought, "Oh. There you are. I've been waiting for you." I promise to never make you wait for anything again — not for love, not for support, not for someone who sees your brilliance the way I do.</p>
      </Quote>

      <H2>Lighthearted & Fun</H2>
      <Quote>
        <p>I promise to be your partner in every sense of the word — your dance partner, your road trip partner, your "should we get a dog?" partner (yes, we should), and your "whose family do we visit for Thanksgiving?" partner. I promise to always be on your team, even when your team is wrong. I love you, and I can't wait to annoy you for the rest of our lives.</p>
      </Quote>

      <H2>Short & Powerful</H2>
      <Quote>
        <p>You are my home, my heart, and my greatest adventure. I choose you — today and always. Not because the world says I can, but because my heart says I must.</p>
      </Quote>

      <H2>Writing Tips</H2>
      <ul>
        <li><strong>Your vows don't have to reference your identity:</strong> They can simply be about your love</li>
        <li><strong>But they can if you want:</strong> Acknowledging the journey can be incredibly powerful</li>
        <li><strong>Focus on your partner:</strong> What you love about them specifically, not about love in general</li>
        <li><strong>Avoid templates that feel gendered:</strong> Customize any example to fit your relationship</li>
        <li><strong>Be yourselves:</strong> The most moving vows are always the most authentic ones</li>
      </ul>

      <CTA onGetStarted={onGetStarted} />
    </>),
  },

  '/how-to-write-your-own-vows': {
    title: 'How to Write Your Own Wedding Vows — A Complete Step-by-Step Guide',
    description: 'A complete guide to writing personal wedding vows. Includes prompts, structure, timing tips, and common mistakes to avoid.',
    render: (onGetStarted) => (<>
      <h1 className="commercial-heading mb-3">How to Write Your Own Wedding Vows</h1>
      <p>Writing your own vows is one of the most meaningful things you can do for your wedding — and one of the most intimidating. This guide breaks it down into manageable steps so you can write vows that are personal, polished, and from the heart.</p>

      <CTA onGetStarted={onGetStarted} />

      <H2>Step 1: Brainstorm (Don't Write Yet)</H2>
      <p>Before you try to write anything polished, just answer these questions honestly:</p>
      <ul>
        <li>When did you know you were in love?</li>
        <li>What's your favorite memory together?</li>
        <li>What does your partner do that no one else does?</li>
        <li>What have you learned from being with them?</li>
        <li>What are you most excited about for your future?</li>
        <li>What do you want to promise them?</li>
        <li>What do you admire most about them?</li>
        <li>How has your partner changed you for the better?</li>
      </ul>
      <p>Write down your raw answers. Don't edit. Don't judge. Just get it on paper.</p>

      <H2>Step 2: Find Your Structure</H2>
      <p>Most great vows follow a simple three-part structure:</p>
      <ol>
        <li><strong>The Reflection:</strong> What your partner means to you (past/present)</li>
        <li><strong>The Promises:</strong> What you commit to going forward</li>
        <li><strong>The Declaration:</strong> A closing statement of love and commitment</li>
      </ol>

      <H3>Template:</H3>
      <Quote>
        <p><strong>[Reflection]</strong> When I met you / Before you / Since you came into my life...</p>
        <p><strong>[Promises]</strong> I promise to... (2-4 specific commitments)</p>
        <p><strong>[Declaration]</strong> You are my... / I choose you... / Today and always...</p>
      </Quote>

      <H2>Step 3: Write Your First Draft</H2>
      <p>Using your brainstorm notes and the structure above, write a first draft. Don't worry about perfection. Key guidelines:</p>
      <ul>
        <li><strong>Aim for 150-300 words</strong> (1-2 minutes spoken)</li>
        <li><strong>Write like you talk</strong> — your partner fell in love with your voice, not Shakespeare's</li>
        <li><strong>Be specific</strong> — "I love the way you read to the kids every night" beats "I love what a great parent you are"</li>
        <li><strong>Include at least 2 concrete promises</strong> — not just "I'll love you forever" but "I'll always make sure you know how valued you are"</li>
      </ul>

      <H2>Step 4: Refine & Edit</H2>
      <ul>
        <li>Read it aloud — multiple times. Awkward phrasing becomes obvious when spoken.</li>
        <li>Cut anything that feels generic or could apply to anyone</li>
        <li>Make sure you're speaking TO your partner, not about them</li>
        <li>Check the emotional arc — it should build toward the end</li>
        <li>Get feedback from one trusted person (not your partner!)</li>
      </ul>

      <H2>Step 5: Practice Delivery</H2>
      <ul>
        <li><strong>Print it on a nice card</strong> — don't read from your phone</li>
        <li>Practice reading it aloud 5-10 times</li>
        <li>Time yourself — adjust if over 2 minutes</li>
        <li>Plan for tears — they will come. Pause, breathe, continue.</li>
        <li>Make eye contact between glances at your card</li>
      </ul>

      <H2>Common Mistakes to Avoid</H2>
      <ul>
        <li><strong>Too long:</strong> Anything over 3 minutes loses the audience</li>
        <li><strong>Too many inside jokes:</strong> One is charming. Five is alienating.</li>
        <li><strong>Roasting your partner:</strong> A light joke is fine. A comedy roast is not.</li>
        <li><strong>Being vague:</strong> "I love everything about you" is nice but forgettable</li>
        <li><strong>Forgetting promises:</strong> Vows should include actual commitments, not just declarations</li>
        <li><strong>Waiting until the last minute:</strong> Give yourself at least 2-4 weeks</li>
        <li><strong>Mismatched length:</strong> Coordinate with your partner on approximate length</li>
      </ul>

      <CTA onGetStarted={onGetStarted} />
    </>),
  },

  '/wedding-vows-template-nervous-speakers': {
    title: 'Wedding Vows Template for Nervous Speakers',
    description: 'Simple fill-in-the-blank wedding vow templates for people who are nervous about speaking. Short, manageable, and beautiful.',
    render: (onGetStarted) => (<>
      <h1 className="commercial-heading mb-3">Wedding Vows for Nervous Speakers</h1>
      <p>If the thought of speaking in front of a crowd makes your palms sweat, you're not alone. More than half of all people rank public speaking as their number one fear — above spiders, heights, and even death. Here are templates designed to be short, manageable, and still deeply meaningful.</p>

      <CTA onGetStarted={onGetStarted} />

      <H2>Template 1: The Three Promises (Under 60 Words)</H2>
      <Quote>
        <p>[NAME], you are my best friend, my greatest love, and my home.</p>
        <p>I promise to [PROMISE 1 — e.g., always be honest with you].</p>
        <p>I promise to [PROMISE 2 — e.g., support your dreams as fiercely as my own].</p>
        <p>And I promise to [PROMISE 3 — e.g., love you more with every passing year].</p>
        <p>Today and always, I am yours.</p>
      </Quote>

      <H2>Template 2: Past, Present, Future (Under 80 Words)</H2>
      <Quote>
        <p>From the moment [REFERENCE TO WHEN YOU FELL IN LOVE — e.g., you laughed at my terrible joke on our first date], I knew you were special.</p>
        <p>Today, I stand here knowing you are the love of my life.</p>
        <p>And tomorrow, and every day after, I promise to [ONE BIG PROMISE — e.g., be the partner you deserve].</p>
        <p>I love you, [NAME]. I always will.</p>
      </Quote>

      <H2>Template 3: The Simple Declaration (Under 50 Words)</H2>
      <Quote>
        <p>[NAME], I love you. I am proud to be with you. I promise to stand by you, to lift you up, and to build a beautiful life with you. You are my person, and I am yours. Forever.</p>
      </Quote>

      <H2>Template 4: The List (Structured & Easy to Read)</H2>
      <Quote>
        <p>[NAME], I promise you these things:</p>
        <p>To love you on the good days and the hard days.</p>
        <p>To listen when you need to talk and hold you when you don't.</p>
        <p>To never stop learning who you are.</p>
        <p>To be your partner in everything.</p>
        <p>And to always, always come home to you.</p>
      </Quote>

      <H2>Tips for Managing Nerves</H2>
      <H3>Before the ceremony:</H3>
      <ul>
        <li><strong>Practice 10+ times</strong> — familiarity breeds confidence</li>
        <li><strong>Practice in front of a mirror</strong> or record yourself</li>
        <li><strong>Practice with an audience</strong> — read them to a friend, your pet, or an empty room</li>
        <li><strong>Don't memorize word-for-word</strong> — bring a card and give yourself permission to read</li>
      </ul>

      <H3>During the ceremony:</H3>
      <ul>
        <li><strong>Breathe:</strong> Take a deep breath before you start. Nobody is timing you.</li>
        <li><strong>Look at your partner:</strong> The crowd disappears when you focus on one person</li>
        <li><strong>It's okay to cry:</strong> Pause, breathe, smile, continue. Tears show love, not weakness.</li>
        <li><strong>Speak slowly:</strong> Nerves make us rush. Consciously slow down by 50%.</li>
        <li><strong>Hold the card in both hands:</strong> This hides trembling</li>
      </ul>

      <H3>The secret truth:</H3>
      <p>Nobody in the audience is judging your delivery. They're thinking about how beautiful this moment is. They're crying too. Your vows don't need to be perfect — they just need to be real.</p>

      <CTA onGetStarted={onGetStarted} />
    </>),
  },

  '/best-opening-lines-wedding-vows': {
    title: 'Best Opening Lines for Wedding Vows — 20+ Ways to Start',
    description: '20+ powerful opening lines for wedding vows organized by tone. Set the perfect tone for your ceremony.',
    render: (onGetStarted) => (<>
      <h1 className="commercial-heading mb-3">Best Opening Lines for Wedding Vows</h1>
      <p>The first words of your vows set the tone for everything that follows. A great opening line captures attention, establishes emotion, and gives you momentum to keep going. Here are 20+ options organized by tone.</p>

      <CTA onGetStarted={onGetStarted} />

      <H2>Romantic Openers</H2>
      <Quote><p>"From the moment I met you, I knew my life would never be the same."</p></Quote>
      <Quote><p>"You are the love I didn't know I was looking for."</p></Quote>
      <Quote><p>"I have loved you in a hundred quiet ways, and today I finally get to say it out loud."</p></Quote>
      <Quote><p>"Standing here, looking at you, I am overwhelmed by how lucky I am."</p></Quote>

      <H2>Personal Story Openers</H2>
      <Quote><p>"The first time I saw you, you were [specific scene — laughing at a party, lost in a book, arguing about pizza toppings]..."</p></Quote>
      <Quote><p>"You probably don't remember this, but on our third date, you [small specific moment]..."</p></Quote>
      <Quote><p>"My mom always said I'd know when I found the right person. She was right."</p></Quote>
      <Quote><p>"If you'd told me [X years] ago that I'd be standing here today, I wouldn't have believed you."</p></Quote>

      <H2>Funny Openers</H2>
      <Quote><p>"I googled 'how to write wedding vows' and the first result said to speak from the heart. So here goes nothing."</p></Quote>
      <Quote><p>"I've been trying to write these vows for weeks. I've started over fourteen times. Apparently it's hard to put 'you're the best thing that ever happened to me' into different words."</p></Quote>
      <Quote><p>"I know I'm supposed to be nervous right now, but looking at you, all I feel is sure."</p></Quote>

      <H2>Simple & Direct Openers</H2>
      <Quote><p>"[Name], I love you. And here's why."</p></Quote>
      <Quote><p>"You are my best friend, and today I get to marry you."</p></Quote>
      <Quote><p>"I have three promises to make to you today."</p></Quote>
      <Quote><p>"You are my person. You have been since the moment we met."</p></Quote>

      <H2>Poetic Openers</H2>
      <Quote><p>"In a world of billions, the universe conspired to bring me to you."</p></Quote>
      <Quote><p>"Love is patient, love is kind — and love is standing here, terrified and grateful, promising you forever."</p></Quote>
      <Quote><p>"They say home is where the heart is. My heart has been with you since the day we met."</p></Quote>

      <H2>Grateful Openers</H2>
      <Quote><p>"I don't have the words to thank you for everything you've brought into my life. But today I'll try."</p></Quote>
      <Quote><p>"Thank you for choosing me. Thank you for seeing me. Thank you for loving me. Now let me tell you why I choose you."</p></Quote>

      <H2>How Your Opening Sets the Tone</H2>
      <ul>
        <li><strong>Romantic opening → emotional vows:</strong> Starts intimate and builds</li>
        <li><strong>Funny opening → heartfelt close:</strong> The contrast creates the most powerful emotional impact</li>
        <li><strong>Direct opening → confident vows:</strong> Shows clarity and conviction</li>
        <li><strong>Story opening → personal vows:</strong> Grounds the vows in your specific relationship</li>
      </ul>

      <H2>What to Avoid in Your Opening</H2>
      <ul>
        <li><strong>Don't start with a definition:</strong> "Webster's dictionary defines love as..." is the cliché of all clichés</li>
        <li><strong>Don't start with an apology:</strong> "I'm not good at this" undermines everything that follows</li>
        <li><strong>Don't start with someone else's words:</strong> Open with yours, save quotes for the middle or end</li>
        <li><strong>Don't start with a long story:</strong> Your opening should be one or two sentences, then transition</li>
      </ul>

      <CTA onGetStarted={onGetStarted} />
    </>),
  },
};

// ─── SEO PAGE PATHS (exported for routing) ──────────────────────

export const SEO_PATHS = Object.keys(PAGES);

// ─── COMPONENT ──────────────────────────────────────────────────

export default function SeoPage({ onGetStarted }) {
  const path = window.location.pathname;
  const page = PAGES[path];

  useEffect(() => {
    if (page) {
      document.title = page.title + ' | VowAuthor';
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
      meta.content = page.description;

      const ld = document.createElement('script');
      ld.type = 'application/ld+json';
      ld.text = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: page.title,
        description: page.description,
        publisher: { '@type': 'Organization', name: 'VowAuthor', url: 'https://vowauthor.com' },
        datePublished: '2026-03-31',
        dateModified: '2026-03-31',
        mainEntityOfPage: `https://vowauthor.com${path}`,
      });
      document.head.appendChild(ld);
      window.scrollTo(0, 0);
      return () => { document.head.removeChild(ld); };
    }
  }, [path, page]);

  if (!page) {
    return (
      <div className="container py-5 text-center">
        <div className="glass-card">
          <h1 className="commercial-heading">Page Not Found</h1>
          <p>The page you're looking for doesn't exist.</p>
          <a href="/" style={{ color: '#1e3a5f' }}>Return home</a>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-10 col-xl-8">
          <div className="glass-card" style={{ lineHeight: 1.7 }}>
            {page.render(onGetStarted)}
            <RelatedLinks current={path} />
          </div>
        </div>
      </div>
    </div>
  );
}
